import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Code,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { MdArrowBack, MdRefresh } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import { RUN_STATUS_COLOR, STEP_STATUS_COLOR } from "./templates";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function RunDetail({ workflowId, runId, baseUrl, onClose }) {
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("loading");

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/workflow/${workflowId}/runs/${runId}`,
        "GET"
      );
      setDetail(res?.data);
      setStatus("success");
    } catch { setStatus("error"); }
  }, [baseUrl, workflowId, runId]);

  useEffect(() => { load(); }, [load]);

  if (status === "loading") return <Spinner />;
  if (!detail) return <Text color="red.500">Failed to load run.</Text>;

  return (
    <Card variant="outline" mt={2}>
      <CardBody>
        <Flex justify="space-between" align="center" mb={2}>
          <Box>
            <Text fontSize="xs" color="gray.500">Run id</Text>
            <Code fontSize="xs">{detail.id}</Code>
          </Box>
          <Button size="xs" leftIcon={<MdRefresh />} onClick={load} variant="ghost">Refresh</Button>
        </Flex>
        <Flex gap={6} mb={3} fontSize="sm">
          <Box>
            <Text color="gray.500" fontSize="xs">Status</Text>
            <Badge colorScheme={RUN_STATUS_COLOR[detail.status] || "gray"}>{detail.status}</Badge>
          </Box>
          <Box>
            <Text color="gray.500" fontSize="xs">Started</Text>
            {fmt(detail.started_at)}
          </Box>
          <Box>
            <Text color="gray.500" fontSize="xs">Finished</Text>
            {fmt(detail.finished_at)}
          </Box>
          <Box>
            <Text color="gray.500" fontSize="xs">Current node</Text>
            {detail.current_node_id || "—"}
          </Box>
        </Flex>

        {detail.error && (
          <Box bg="red.50" p={2} mb={3} borderRadius="md">
            <Text fontSize="xs" fontWeight="bold" color="red.600">Error</Text>
            <Code fontSize="xs" colorScheme="red">{detail.error}</Code>
          </Box>
        )}

        {Object.keys(detail.trigger_payload || {}).length > 0 && (
          <Box mb={3}>
            <Text fontSize="xs" color="gray.500" mb={1}>Trigger payload</Text>
            <Code display="block" fontSize="xs" whiteSpace="pre-wrap" p={2}>
              {JSON.stringify(detail.trigger_payload, null, 2)}
            </Code>
          </Box>
        )}

        {Object.keys(detail.context || {}).length > 0 && (
          <Box mb={3}>
            <Text fontSize="xs" color="gray.500" mb={1}>Context</Text>
            <Code display="block" fontSize="xs" whiteSpace="pre-wrap" p={2}>
              {JSON.stringify(detail.context, null, 2)}
            </Code>
          </Box>
        )}

        <Text fontSize="sm" fontWeight="semibold" mb={2}>Step timeline</Text>
        {(detail.step_logs || []).length === 0 ? (
          <Text fontSize="sm" color="gray.500">No steps recorded.</Text>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th w="50px">#</Th>
                <Th>Node</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Output</Th>
              </Tr>
            </Thead>
            <Tbody>
              {detail.step_logs.map((s, i) => (
                <Tr key={s.id}>
                  <Td>{i + 1}</Td>
                  <Td><Code fontSize="xs">{s.step_id}</Code></Td>
                  <Td><Code fontSize="xs">{s.node_type}</Code></Td>
                  <Td>
                    <Badge colorScheme={STEP_STATUS_COLOR[s.status] || "gray"} fontSize="xs">
                      {s.status}
                    </Badge>
                  </Td>
                  <Td>
                    {s.error ? (
                      <Code fontSize="xs" colorScheme="red">{s.error}</Code>
                    ) : Object.keys(s.output || {}).length > 0 ? (
                      <Code fontSize="xs" whiteSpace="pre-wrap" maxW="400px">
                        {JSON.stringify(s.output)}
                      </Code>
                    ) : "—"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}

        {(detail.waits || []).filter((w) => w.status === "pending").length > 0 && (
          <Box mt={3}>
            <Text fontSize="sm" fontWeight="semibold" mb={1}>Pending waits</Text>
            {detail.waits.filter((w) => w.status === "pending").map((w) => (
              <Box key={w.id} fontSize="xs" color="gray.600">
                Resume at <Code>{w.resume_node_id}</Code> on {fmt(w.due_at)}
              </Box>
            ))}
          </Box>
        )}
      </CardBody>
    </Card>
  );
}

export default function WorkflowRuns() {
  const baseUrl = getHostDomain();
  const navigate = useNavigate();
  const { workflowId } = useParams();

  const [wf, setWf] = useState(null);
  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState("idle");
  const [openRunId, setOpenRunId] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const [wfRes, runsRes] = await Promise.all([
        fetchController(`${baseUrl}/api/v1/workflow/${workflowId}`, "GET"),
        fetchController(`${baseUrl}/api/v1/workflow/${workflowId}/runs?size=50`, "GET"),
      ]);
      setWf(wfRes?.data);
      setRuns(runsRes?.data?.items || []);
      setStatus("success");
    } catch { setStatus("error"); }
  }, [baseUrl, workflowId]);

  useEffect(() => { load(); }, [load]);

  return (
    <VStack align="stretch" spacing={3} h="100%">
      <Flex align="center" gap={3}>
        <IconButton icon={<MdArrowBack />} variant="ghost" aria-label="Back"
          onClick={() => navigate(`/library/workflows/${workflowId}`)} />
        <Box flex="1">
          <Heading size="md">{wf ? `Runs — ${wf.name}` : "Runs"}</Heading>
          <Text fontSize="sm" color="gray.500">
            Newest first. Click a row to inspect the step-by-step log.
          </Text>
        </Box>
        <Button leftIcon={<MdRefresh />} size="sm" variant="outline" onClick={load}>
          Refresh
        </Button>
      </Flex>

      <Card variant="elevated">
        <CardBody p={0}>
          {status === "loading" ? (
            <Flex justify="center" py={10}><Spinner /></Flex>
          ) : runs.length === 0 ? (
            <Flex justify="center" py={10}>
              <Text color="gray.500">No runs yet.</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Started</Th>
                    <Th>Status</Th>
                    <Th>Current Node</Th>
                    <Th>Run id</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {runs.map((r) => (
                    <Fragment key={r.id}>
                      <Tr _hover={{ bg: "gray.50" }}
                          cursor="pointer"
                          onClick={() => setOpenRunId(openRunId === r.id ? null : r.id)}>
                        <Td whiteSpace="nowrap">{fmt(r.started_at)}</Td>
                        <Td>
                          <Badge colorScheme={RUN_STATUS_COLOR[r.status] || "gray"}>
                            {r.status}
                          </Badge>
                        </Td>
                        <Td><Code fontSize="xs">{r.current_node_id || "—"}</Code></Td>
                        <Td><Code fontSize="xs">{r.id}</Code></Td>
                      </Tr>
                      {openRunId === r.id && (
                        <Tr>
                          <Td colSpan={4} bg="gray.50" p={3}>
                            <RunDetail
                              workflowId={workflowId}
                              runId={r.id}
                              baseUrl={baseUrl}
                              onClose={() => setOpenRunId(null)}
                            />
                          </Td>
                        </Tr>
                      )}
                    </Fragment>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </VStack>
  );
}
